<template>
  <div class="container-fluid">
    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-5">
      <div class="loader-box">
        <div class="loader-3"></div>
      </div>
      <p>Cargando...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <h5>Error al cargar los datos</h5>
        <p>{{ error.message || 'Ocurrió un error inesperado' }}</p>
        <button class="btn btn-primary" @click="fetchInstrumentos()">Reintentar</button>
      </div>
    </div>

    <!-- Contenido -->
    <div v-else>    
      <!-- Buscador y filtros -->
      <div class="col-sm-12 col-lg-12 mt-3">
        <div class="card">
          <div class="card-body">
            <!-- Buscador -->
            <div class="d-flex">
              <input
                type="text"
                class="form-control me-2"
                placeholder="Busca un instrumento..."
                v-model="searchQuery"
              />
              <button class="btn btn-primary" @click="searchQuery = searchQuery">
                <i class="fa fa-search"></i> Buscar
              </button>
            </div>

            <!-- Filtros por tipo dinámicos -->
            <div class="mt-3" v-if="tipos.length">
              <span class="me-2 fw-bold">Tipo:</span>
              <button
                v-for="tipo in tipos"
                :key="tipo"
                class="btn me-1 mb-1"
                :class="tipoActivo === tipo ? 'btn-primary btn-xs' : 'btn-outline-primary btn-xs'"
                @click="filtrarPorTipo(tipo)"
              >
                {{ tipo }}
              </button>
              <button
                v-if="tipoActivo"
                class="btn btn-xs btn-outline-secondary mb-1"
                @click="filtrarPorTipo(null)"
              >
                <i class="fa fa-times"></i> Limpiar
              </button>
            </div>

            <!-- Filtros por sector -->
            <div class="mt-2" v-if="sectores.length">
              <span class="me-2 fw-bold">Sector:</span>
              <button
                v-for="sector in sectores"
                :key="sector.id"
                class="btn me-1 mb-1"
                :class="sectorActivo === sector.id ? 'btn-primary btn-xs' : 'btn-outline-primary btn-xs'"
                @click="filtrarPorSector(sector.id)"
              >
                {{ sector.nombre }}
              </button>
              <button
                v-if="sectorActivo"
                class="btn btn-xs btn-outline-secondary mb-1"
                @click="filtrarPorSector(null)"
              >
                <i class="fa fa-times"></i> Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Sin resultados -->
      <div v-if="instrumentosFiltrados.length === 0" class="text-center py-5">
        <p class="text-muted">No se encontraron resultados para tu búsqueda.</p>
      </div>

      <!-- Grid de instrumentos -->
      <div id="instrumentos-list" class="row starter-main">
        <div
          v-for="instrumento in instrumentosPaginados"
          :key="instrumento.id"
          class="col-sm-4 mb-3"
        >
          <div
            class="card"
            style="cursor: pointer; transition: box-shadow 0.2s; border-radius: 15px; overflow: hidden;"
            @mouseenter="e => e.currentTarget.style.boxShadow='0 4px 24px rgba(16,46,82,0.18)'"
            @mouseleave="e => e.currentTarget.style.boxShadow=''"
          >
            <div class="card-header" style="background-color: #09377c; border-bottom: 1px solid #ecf3fa; padding: 20px;">
              <h5 class="mb-1" style="color: #fff;">{{ instrumento.title || instrumento.nombre }}</h5>
              <template v-if="instrumento.tipo">
                <span
                  v-for="(t, i) in normChips(instrumento.tipo)"
                  :key="'tipo-'+i"
                  class="badge mt-2 me-1 d-inline-block"
                  style="background-color: rgba(255,255,255,0.2); color: #fff; font-size: 0.75rem; white-space: nowrap;"
                >{{ t }}</span>
              </template>
              <template v-if="instrumento.sector?.length">
                <span
                  v-for="(s, i) in normChips(instrumento.sector)"
                  :key="'sec-'+i"
                  class="badge mt-2 me-1 d-inline-block"
                  style="background-color: rgba(255,255,255,0.15); color: #fff; font-size: 0.75rem; white-space: nowrap;"
                >{{ s }}</span>
              </template>
              <template v-if="instrumento.escala?.length">
                <span
                  v-for="(e, i) in normChips(instrumento.escala)"
                  :key="'esc-'+i"
                  class="badge mt-2 me-1 d-inline-block bg-success"
                  style="font-size: 0.75rem; white-space: nowrap;"
                >{{ e }}</span>
              </template>
            </div>
            <div class="card-body" style="padding: 20px;">
              <p v-if="instrumento.descripcion || instrumento.description || instrumento.intro" class="small text-muted mb-0" style="display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;">
                {{ stripHtml(instrumento.descripcion || instrumento.description || instrumento.intro) }}
              </p>
            </div>
            <div class="card-footer" style="border-top: 1px solid #ecf3fa; padding: 20px;">
              <div class="text-end">
                <router-link
                  :to="{ name: 'detalleInstrumento', params: { id: instrumento.id } }"
                  class="btn btn-outline-primary btn-sm"
                  style="transition: background-color 0.2s, color 0.2s;"
                  @mouseenter="e => { e.currentTarget.style.backgroundColor='#09377c'; e.currentTarget.style.color='#fff'; e.currentTarget.style.borderColor='#09377c'; }"
                  @mouseleave="e => { e.currentTarget.style.backgroundColor=''; e.currentTarget.style.color=''; e.currentTarget.style.borderColor=''; }"
                >
                  Ver detalle
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Paginación -->
      <div v-if="totalPages > 1" class="row">
        <div class="col-12 d-flex justify-content-between align-items-center mt-2 mb-4 px-3">
          <span class="text-muted small">
            Mostrando {{ rangoInicio }}–{{ rangoFin }} de {{ instrumentosFiltrados.length }} instrumentos
          </span>
          <nav aria-label="Paginación de instrumentos">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">&laquo;</button>
              </li>
              <li
                v-for="page in visiblePages"
                :key="page"
                class="page-item"
                :class="{ active: page === currentPage, disabled: page === '…' }"
              >
                <button class="page-link" @click="page !== '…' && goToPage(page)" :disabled="page === '…'">
                  {{ page }}
                </button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">&raquo;</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from "@/axios";
import defaultImageSrc from "@/assets/images/verde/ecobanner.webp";
const PAGE_SIZE = 9;

export default {
  name: "InstrumentosPage",
  data() {
    return {
      instrumentos: [],
      error: null,
      loading: true,
      searchQuery: "",
      tipoActivo:    null,
      sectorActivo:  null,
      currentPage:   1,
    };
  },
  computed: {
    defaultImage() {
      return defaultImageSrc;
    },
    tipos() {
      const set = new Set();
      this.instrumentos.forEach((i) => {
        const t = i.tipo || i.type;
        if (!t) return;
        const nombre = typeof t === "object" ? t.nombre : t;
        if (nombre) set.add(nombre);
      });
      return [...set].sort();
    },
    sectores() {
      const map = new Map();
      this.instrumentos.forEach((inst) => {
        (Array.isArray(inst.sector) ? inst.sector : []).forEach((s) => {
          if (s?.id != null && !map.has(s.id)) map.set(s.id, s);
        });
      });
      return [...map.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
    },
    instrumentosFiltrados() {
      return this.instrumentos.filter((instrumento) => {
        const tipoNombre = (v) => (v && typeof v === "object" ? v.nombre : v) || "";
        const matchTipo = !this.tipoActivo || tipoNombre(instrumento.tipo || instrumento.type) === this.tipoActivo;
        const matchSector = !this.sectorActivo ||
          (Array.isArray(instrumento.sector) && instrumento.sector.some((s) => s.id === this.sectorActivo));
        const q = this.searchQuery.toLowerCase();
        const nombresArr = (arr) =>
          (Array.isArray(arr) ? arr : []).map((x) => (typeof x === "object" ? x.nombre : x)).join(" ");
        const matchSearch =
          !q ||
          (instrumento.title || instrumento.nombre || "").toLowerCase().includes(q) ||
          (instrumento.intro || instrumento.descripcion || instrumento.description || "").toLowerCase().includes(q) ||
          tipoNombre(instrumento.tipo || instrumento.type).toLowerCase().includes(q) ||
          nombresArr(instrumento.sector).toLowerCase().includes(q) ||
          nombresArr(instrumento.estado).toLowerCase().includes(q) ||
          nombresArr(instrumento.escala).toLowerCase().includes(q);
        return matchTipo && matchSector && matchSearch;
      });
    },
    totalPages() {
      return Math.ceil(this.instrumentosFiltrados.length / PAGE_SIZE);
    },
    rangoInicio() {
      return (this.currentPage - 1) * PAGE_SIZE + 1;
    },
    rangoFin() {
      return Math.min(this.currentPage * PAGE_SIZE, this.instrumentosFiltrados.length);
    },
    instrumentosPaginados() {
      const start = (this.currentPage - 1) * PAGE_SIZE;
      return this.instrumentosFiltrados.slice(start, start + PAGE_SIZE);
    },
    visiblePages() {
      const total = this.totalPages;
      const cur   = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      if (cur <= 4)         return [1, 2, 3, 4, 5, '…', total];
      if (cur >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
      return [1, '…', cur - 1, cur, cur + 1, '…', total];
    },
  },
  watch: {
    searchQuery()  { this.currentPage = 1; },
    tipoActivo()   { this.currentPage = 1; },
    sectorActivo() { this.currentPage = 1; },
  },
  created() {
    this.fetchInstrumentos();
  },
  methods: {
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.currentPage = page;
      document.getElementById('instrumentos-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    filtrarPorTipo(tipo) {
      this.tipoActivo = tipo;
    },
    filtrarPorSector(id) {
      this.sectorActivo = id;
    },
    normChips(val) {
      if (Array.isArray(val)) return val.map((x) => (typeof x === "object" ? x.nombre : x)).filter(Boolean);
      if (typeof val === "string" && val) return [val];
      return [];
    },
    stripHtml(html) {
      if (!html) return "";
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    },
    async fetchInstrumentos() {
      this.loading = true;
      this.error = null;
      try {
        let todos = [];
        let page = 1;
        let hayMas = true;
        while (hayMas) {
          const { data } = await api.get("instrumentos/", { params: { page } });
          if (Array.isArray(data)) {
            todos = [...todos, ...data];
            hayMas = false;
          } else {
            const items = data.results ?? data.items ?? [];
            todos = [...todos, ...items];
            hayMas = !!data.next && items.length > 0;
            page++;
          }
        }
        this.instrumentos = todos;
      } catch (err) {
        this.error = err;
        console.error("Error al cargar instrumentos:", err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
